import * as React from "react"
import { createPortal } from "react-dom"
import { cn } from "../../lib/utils"

// Context lets DropdownMenuItem (rendered deep inside the portaled
// DropdownMenuContent) close the menu via setOpen, without prop-drilling
// through arbitrary intermediate DOM that consumers may put inside Content.
const DropdownMenuContext = React.createContext(null)

const DropdownMenu = ({ children, open: controlledOpen, onOpenChange }) => {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const triggerRef = React.useRef(null)

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? onOpenChange : setInternalOpen

  const ctx = React.useMemo(() => ({ open, setOpen, triggerRef }), [open, setOpen])

  return (
    <DropdownMenuContext.Provider value={ctx}>
      <div className="relative inline-block text-left">
        {React.Children.map(children, child => {
          if (!React.isValidElement(child)) return child

          // If the direct child is a Fragment (e.g. wrapping multiple items),
          // do NOT pass props to the Fragment itself – only to its children.
          if (child.type === React.Fragment) {
            return (
              <React.Fragment key={child.key}>
                {React.Children.map(child.props.children, nestedChild => {
                  if (!React.isValidElement(nestedChild)) return nestedChild
                  return React.cloneElement(nestedChild, { open, setOpen, triggerRef })
                })}
              </React.Fragment>
            )
          }

          return React.cloneElement(child, { open, setOpen, triggerRef })
        })}
      </div>
    </DropdownMenuContext.Provider>
  )
}

const DropdownMenuTrigger = React.forwardRef(({ className, children, asChild, open, setOpen, triggerRef, ...props }, ref) => {
  const handleClick = () => setOpen(!open)
  
  const buttonRef = React.useCallback((node) => {
    if (triggerRef) {
      triggerRef.current = node
    }
    if (ref) {
      if (typeof ref === 'function') {
        ref(node)
      } else {
        ref.current = node
      }
    }
  }, [ref, triggerRef])
  
  if (asChild) {
    return React.cloneElement(children, {
      ...props,
      ref: buttonRef,
      onClick: handleClick,
    })
  }
  
  return (
    <button
      ref={buttonRef}
      onClick={handleClick}
      className={cn("focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", className)}
      {...props}
    >
      {children}
    </button>
  )
})
DropdownMenuTrigger.displayName = "DropdownMenuTrigger"

const DropdownMenuContent = React.forwardRef(({ className, align = "center", side = "bottom", children, open, setOpen, triggerRef, ...props }, ref) => {
  const contentRef = React.useRef(null)
  const [position, setPosition] = React.useState({ top: 0, left: 0 })
  
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (contentRef.current && !contentRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    
    if (open) {
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [open, setOpen])
  
  React.useEffect(() => {
    if (open && triggerRef?.current) {
      const updatePosition = () => {
        const trigger = triggerRef.current
        if (!trigger) return

        const rect = trigger.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        const padding = 8 // Minimum distance from viewport edges

        // Get actual content dimensions if available, otherwise use estimates
        const contentWidth = contentRef.current?.offsetWidth || 192 // min-w-[12rem] on mobile
        const contentHeight = contentRef.current?.offsetHeight || 200

        let top = rect.bottom + 8 // mt-2 = 8px
        let left = rect.right - contentWidth // align="end"

        if (side === 'top') {
          top = rect.top - 8 // mb-2 = 8px
          if (contentRef.current) {
            top -= contentRef.current.offsetHeight
          }
        }

        if (align === 'start') {
          left = rect.left
        } else if (align === 'center') {
          left = rect.left + (rect.width / 2) - (contentWidth / 2)
        }

        // Clamp left position to stay within viewport
        if (left < padding) {
          left = padding
        } else if (left + contentWidth > viewportWidth - padding) {
          left = viewportWidth - contentWidth - padding
        }

        // Clamp top position to stay within viewport
        if (top < padding) {
          top = padding
        } else if (top + contentHeight > viewportHeight - padding) {
          // If dropdown would go off bottom, try placing it above the trigger
          const topAbove = rect.top - contentHeight - 8
          if (topAbove >= padding) {
            top = topAbove
          } else {
            top = viewportHeight - contentHeight - padding
          }
        }

        setPosition({ top, left })
      }

      updatePosition()
      // Update position on scroll or resize
      window.addEventListener('scroll', updatePosition, true)
      window.addEventListener('resize', updatePosition)

      return () => {
        window.removeEventListener('scroll', updatePosition, true)
        window.removeEventListener('resize', updatePosition)
      }
    }
  }, [open, align, side, triggerRef])
  
  if (!open) return null

  const content = (
    <div
      ref={contentRef}
      className={cn(
        "fixed z-50 min-w-[12rem] sm:min-w-[16rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
        className
      )}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      {...props}
    >
      {children}
    </div>
  )

  return createPortal(content, document.body)
})
DropdownMenuContent.displayName = "DropdownMenuContent"

const DropdownMenuItem = React.forwardRef(({ className, onClick, onSelect, setOpen: setOpenProp, children, ...props }, ref) => {
  const ctx = React.useContext(DropdownMenuContext)
  // Prefer the prop (legacy prop-drilled setOpen still works); fall back to
  // the context provided by the nearest DropdownMenu ancestor.
  const setOpen = setOpenProp || ctx?.setOpen
  // onSelect is the Radix name for "item chosen". This menu is not Radix, and
  // before it was handled here an onSelect prop fell through to the <div> as
  // the DOM `select` event, which a click never fires — so Kudos picked from
  // the menu silently did nothing (2026-10-12).
  const handleClick = (e) => {
    onClick?.(e)
    onSelect?.(e)
    setOpen?.(false)
  }

  return (
    <div
      ref={ref}
      onClick={handleClick}
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2.5 sm:px-2 sm:py-1.5 text-base sm:text-sm outline-none transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
})
DropdownMenuItem.displayName = "DropdownMenuItem"

const DropdownMenuSeparator = React.forwardRef(({ className, setOpen, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
))
DropdownMenuSeparator.displayName = "DropdownMenuSeparator"

const DropdownMenuLabel = React.forwardRef(({ className, inset, setOpen, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "px-3 py-2 sm:px-2 sm:py-1.5 text-base sm:text-sm font-semibold",
      inset && "pl-8",
      className
    )}
    {...props}
  />
))
DropdownMenuLabel.displayName = "DropdownMenuLabel"

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
}
